import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {number} */
  #syncRequestId = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
      void this.#syncCartBubblesFromServer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
   if (cartCount !== undefined) {

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

    void this.#syncCartBubblesFromServer();
  };

  #syncCartBubblesFromServer = async () => {
    const requestId = ++this.#syncRequestId;

    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      if (requestId !== this.#syncRequestId) return;

      const count = Number(cart?.item_count || 0);
      if (Number.isNaN(count)) return;

      const countText = count < 100 ? String(count) : '';
      const countNodes = this.querySelectorAll('[ref="cartBubbleCount"]');
      const bubbleNodes = this.querySelectorAll('[ref="cartBubble"]');
      const hasCart = count > 0;

      for (const node of countNodes) {
        if (!(node instanceof HTMLElement)) continue;
        node.textContent = countText;
        node.classList.toggle('hidden', !hasCart);
      }

      for (const node of bubbleNodes) {
        if (!(node instanceof HTMLElement)) continue;
        node.classList.toggle('visually-hidden', !hasCart);
      }

      const cartIcons = this.querySelectorAll('cart-icon');
      for (const icon of cartIcons) {
        if (!(icon instanceof HTMLElement)) continue;
        icon.classList.toggle('header-actions__cart-icon--has-cart', hasCart);
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
